import type { CascadingSelectOption } from './types.js';

export const CASCADING_SELECT_MAX_DEPTH = 5;
export const CASCADING_SELECT_MAX_NODES = 500;
export const CASCADING_SELECT_MAX_SIBLINGS = 100;

export type CascadingTreeIssue =
  | 'empty_roots'
  | 'too_many_siblings'
  | 'too_many_nodes'
  | 'too_deep'
  | 'empty_children'
  | 'empty_label'
  | 'label_too_long'
  | 'label_not_nfc'
  | 'duplicate_id';

export function normalizeCascadingLabel(label: string): string {
  return label.normalize('NFC');
}

export function cascadingNodeCount(options: readonly CascadingSelectOption[]): number {
  return options.reduce((count, option) => count + 1 + cascadingNodeCount(option.children ?? []), 0);
}

export function updateCascadingOption(
  options: readonly CascadingSelectOption[],
  nodeId: string,
  update: (option: CascadingSelectOption) => CascadingSelectOption,
): CascadingSelectOption[] {
  return options.map((option) => {
    if (option.id === nodeId) return update(option);
    if (!option.children) return option;
    return { ...option, children: updateCascadingOption(option.children, nodeId, update) };
  });
}

export function appendCascadingChild(
  options: readonly CascadingSelectOption[],
  nodeId: string,
  child: CascadingSelectOption,
): CascadingSelectOption[] {
  return updateCascadingOption(options, nodeId, (option) => ({
    ...option,
    children: [...(option.children ?? []), child],
  }));
}

export function insertCascadingSibling(
  options: readonly CascadingSelectOption[],
  nodeId: string,
  sibling: CascadingSelectOption,
): CascadingSelectOption[] {
  const index = options.findIndex((option) => option.id === nodeId);
  if (index >= 0) {
    const next = [...options];
    next.splice(index + 1, 0, sibling);
    return next;
  }
  return options.map((option) => option.children
    ? { ...option, children: insertCascadingSibling(option.children, nodeId, sibling) }
    : option);
}

export function moveCascadingOption(
  options: readonly CascadingSelectOption[],
  nodeId: string,
  delta: -1 | 1,
): CascadingSelectOption[] {
  const index = options.findIndex((option) => option.id === nodeId);
  if (index >= 0) {
    const target = index + delta;
    if (target < 0 || target >= options.length) return [...options];
    const next = [...options];
    [next[index], next[target]] = [next[target]!, next[index]!];
    return next;
  }
  return options.map((option) => option.children
    ? { ...option, children: moveCascadingOption(option.children, nodeId, delta) }
    : option);
}

export function removeCascadingOption(
  options: readonly CascadingSelectOption[],
  nodeId: string,
): CascadingSelectOption[] {
  return options.flatMap((option) => {
    if (option.id === nodeId) return [];
    if (!option.children) return [option];
    const children = removeCascadingOption(option.children, nodeId);
    const { children: _children, ...withoutChildren } = option;
    return [{ ...withoutChildren, ...(children.length > 0 ? { children } : {}) }];
  });
}

export function firstCascadingPath(options: readonly CascadingSelectOption[]): CascadingSelectOption[] {
  const path: CascadingSelectOption[] = [];
  let candidates = options;
  while (candidates.length > 0) {
    const option = candidates[0];
    if (!option) break;
    path.push(option);
    candidates = option.children ?? [];
  }
  return path;
}

export function cascadingPathLabels(
  options: readonly CascadingSelectOption[],
  path: readonly string[],
): string[] {
  const labels: string[] = [];
  let candidates = options;
  for (const optionId of path) {
    const option = candidates.find((candidate) => candidate.id === optionId);
    if (!option) return [...path];
    labels.push(option.label);
    candidates = option.children ?? [];
  }
  return candidates.length === 0 ? labels : [...path];
}

export function cascadingTreeIssue(options: readonly CascadingSelectOption[]): CascadingTreeIssue | null {
  if (options.length === 0) return 'empty_roots';
  if (options.length > CASCADING_SELECT_MAX_SIBLINGS) return 'too_many_siblings';
  const ids = new Set<string>();
  let total = 0;
  let issue: CascadingTreeIssue | null = null;
  const visit = (siblings: readonly CascadingSelectOption[], depth: number) => {
    if (issue) return;
    if (siblings.length > CASCADING_SELECT_MAX_SIBLINGS) {
      issue = 'too_many_siblings';
      return;
    }
    if (depth > CASCADING_SELECT_MAX_DEPTH) {
      issue = 'too_deep';
      return;
    }
    for (const option of siblings) {
      total += 1;
      if (total > CASCADING_SELECT_MAX_NODES) {
        issue = 'too_many_nodes';
        return;
      }
      if (ids.has(option.id)) {
        issue = 'duplicate_id';
        return;
      }
      ids.add(option.id);
      if (option.label.trim().length === 0) {
        issue = 'empty_label';
        return;
      }
      if (option.label.length > 100) {
        issue = 'label_too_long';
        return;
      }
      if (option.label !== option.label.normalize('NFC')) {
        issue = 'label_not_nfc';
        return;
      }
      if (option.children) {
        if (option.children.length === 0) {
          issue = 'empty_children';
          return;
        }
        visit(option.children, depth + 1);
        if (issue) return;
      }
    }
  };
  visit(options, 1);
  return issue;
}
